import React, { createContext, useState, useEffect, useCallback, useContext, useRef } from 'react';
import { jwtDecode } from 'jwt-decode';
import * as api from '../api';
import { useToast } from './ToastContext';

const AuthContext = createContext(null);

const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes
const TOKEN_REFRESH_INTERVAL = 14 * 60 * 1000; // 14 minutes (refresh well before 30m expiration)

export const AuthProvider = ({ children }) => {
    const { showToast } = useToast();
    const [token, setToken] = useState(localStorage.getItem('token'));
    const [user, setUser] = useState(null);
    const [isSessionActive, setIsSessionActive] = useState(!!token);
    const [isRetryingRefresh, setIsRetryingRefresh] = useState(false);

    const lastActivity = useRef(Date.now());
    const inactivityTimer = useRef(null);
    const refreshTimer = useRef(null);

    const [sessionStartTime, setSessionStartTime] = useState(null);
    const lastRefreshTime = useRef(Date.now());

    const logout = useCallback(() => {
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
        setIsSessionActive(false);
        setSessionStartTime(null);
    }, []);

    const login = useCallback((newToken) => {
        localStorage.setItem('token', newToken);
        setToken(newToken);
        setIsSessionActive(true);
        setSessionStartTime(Date.now());
        lastRefreshTime.current = Date.now();
    }, []);

    const handleActivity = useCallback(() => {
        lastActivity.current = Date.now();
    }, []);

    const checkInactivity = useCallback(() => {
        if (Date.now() - lastActivity.current > INACTIVITY_TIMEOUT) {
            logout();
        }
    }, [logout]);

    const refreshToken = useCallback(async () => {
        if (Date.now() - lastActivity.current < INACTIVITY_TIMEOUT) {
            try {
                const { token: newToken } = await api.refreshTokenAPI();
                // Do not call login(newToken) as it would reset the activity timer.
                localStorage.setItem('token', newToken);
                setToken(newToken);
                lastRefreshTime.current = Date.now();
                setIsRetryingRefresh(false); // Success, stop retrying
            } catch (error) {
                console.error('Failed to refresh token:', error);

                // Check if it's an auth error (401/403) or a network error
                const isAuthError = error.message.includes('401') || error.message.includes('403') || error.message.toLowerCase().includes('invalid token');

                if (isAuthError) {
                    console.error('Auth error detected, logging out.');
                    logout();
                } else {
                    // Network or server error - Retry
                    console.warn('Network/Server error during refresh. Retrying in 1 minute.');
                    showToast("Connection unstable. Retrying session refresh...", "warning");
                    setIsRetryingRefresh(true);
                }
            }
        } else {
            // If we are already inactive, just log out.
            logout();
        }
    }, [logout, showToast]);

    useEffect(() => {
        const events = ['mousemove', 'keydown', 'click', 'scroll'];
        events.forEach(event => window.addEventListener(event, handleActivity));

        return () => {
            events.forEach(event => window.removeEventListener(event, handleActivity));
        };
    }, [handleActivity]);

    useEffect(() => {
        if (isSessionActive) {
            if (!sessionStartTime) {
                setSessionStartTime(Date.now());
            }
            lastActivity.current = Date.now();
            lastRefreshTime.current = Date.now();
            inactivityTimer.current = setInterval(checkInactivity, 60 * 1000); // Check every minute

            const interval = isRetryingRefresh ? 60 * 1000 : TOKEN_REFRESH_INTERVAL; // Retry every minute if failed
            refreshTimer.current = setInterval(refreshToken, interval);
        } else {
            clearInterval(inactivityTimer.current);
            clearInterval(refreshTimer.current);
        }

        return () => {
            clearInterval(inactivityTimer.current);
            clearInterval(refreshTimer.current);
        };
    }, [isSessionActive, checkInactivity, refreshToken, isRetryingRefresh]);

    useEffect(() => {
        if (token) {
            const decodedToken = jwtDecode(token);
            const fetchUser = async () => {
                try {
                    const userData = await api.getUser();
                    setUser({
                        username: decodedToken.username,
                        firstName: userData.firstName || '',
                        lastName: userData.lastName || '',
                        theme: userData.theme || 'dark',
                    });
                } catch (err) {
                    console.error('Failed to fetch user data, logging out.', err);
                    logout();
                }
            };
            fetchUser();
        } else {
            setUser(null);
        }
    }, [token, logout]);

    useEffect(() => {
        document.body.className = '';
        if (user?.theme === 'light') {
            document.body.classList.add('light-theme');
        }
    }, [user?.theme]);

    const updateUser = useCallback(async (userData) => {
        try {
            await api.updateUser(userData);
            setUser(prevUser => ({ ...prevUser, ...userData }));
        } catch (error) {
            console.error('Failed to update user', error);
            throw error;
        }
    }, []);

    const updateTheme = useCallback(async (newTheme) => {
        await updateUser({ theme: newTheme });
    }, [updateUser]);

    const value = {
        token,
        user,
        login,
        logout,
        updateUser,
        updateTheme,
        // Debug info
        sessionStartTime,
        lastActivity, // ref
        lastRefreshTime, // ref
        INACTIVITY_TIMEOUT,
        TOKEN_REFRESH_INTERVAL
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};